import {
  entryToIndication,
  nullableJsonToGeckoMeasurement,
} from 'features/indicationTriage'
import { useRolodexEntries } from 'features/rolodex'
import {
  FfsDataValues,
  FfsIndicationData,
  FfsLtaReportData,
  FfsLtaReportDataPartFive,
  FfsLtaReportDataPartFour,
  FfsMawpValues,
  FfsPartFiveFinalAcceptability,
  FfsPartFiveFlawSizeCriteria,
  FfsPartFourCalculatedValues,
  FfsPartFourThicknessCriteria,
  FfsUserParams,
  IndicationWithFfsReport,
} from 'pages/reporting/reports/ffs/types'
import { useMemo } from 'react'
import { GeckoEntry, GeckoMeasurement, RolodexEntryType } from 'types'

const FFS_LTA_REPORT_TYPE = 'ffs-lta-report' as RolodexEntryType

/** Helper to transform nullable measurement JSON to GeckoMeasurement */
function transformMeasurement(
  json: { num: number; unit: string } | null | undefined
): GeckoMeasurement | null {
  if (!json) return null
  return nullableJsonToGeckoMeasurement(json)
}

/** Transform user params from JSON */
function transformUserParams(data: Record<string, unknown>): FfsUserParams {
  const userParams = data.user_params as Record<string, unknown> | undefined
  return {
    fca: transformMeasurement(
      userParams?.fca as { num: number; unit: string } | null
    ),
    fcaMl: transformMeasurement(
      userParams?.fcaMl as { num: number; unit: string } | null
    ),
    selectedMawpCalcThickness:
      (userParams?.selectedMawpCalcThickness as
        | 't_mm'
        | 't_am'
        | 't_c'
        | null) ?? null,
  }
}

/** Transform data values from JSON */
function transformDataValues(data: Record<string, unknown>): FfsDataValues {
  const dataValues = data.data_values as Record<string, unknown> | undefined
  return {
    tmm: transformMeasurement(
      dataValues?.tmm as { num: number; unit: string } | null
    ),
    trd: transformMeasurement(
      dataValues?.trd as { num: number; unit: string } | null
    ),
    tc: transformMeasurement(
      dataValues?.tc as { num: number; unit: string } | null
    ),
    longitudinalFlawLength: transformMeasurement(
      dataValues?.longitudinalFlawLength as { num: number; unit: string } | null
    ),
    latitudinalFlawLength: transformMeasurement(
      dataValues?.latitudinalFlawLength as { num: number; unit: string } | null
    ),
    innerDiameter: transformMeasurement(
      dataValues?.innerDiameter as { num: number; unit: string } | null
    ),
    outerDiameter: transformMeasurement(
      dataValues?.outerDiameter as { num: number; unit: string } | null
    ),
    lambda: transformMeasurement(
      dataValues?.lambda as { num: number; unit: string } | null
    ),
    nominal: transformMeasurement(
      dataValues?.nominal as { num: number; unit: string } | null
    ),
    t_ml: transformMeasurement(
      dataValues?.t_ml as { num: number; unit: string } | null
    ),
    d_ml: transformMeasurement(
      dataValues?.d_ml as { num: number; unit: string } | null
    ),
    corrosionOnInnerSurface:
      (dataValues?.corrosionOnInnerSurface as boolean) ?? false,
  }
}

/** Transform MAWP values from JSON */
function transformMawpValues(data: Record<string, unknown>): FfsMawpValues {
  const mawp = data.mawp as Record<string, unknown> | undefined
  return {
    e: transformMeasurement(mawp?.e as { num: number; unit: string } | null),
    y: transformMeasurement(mawp?.y as { num: number; unit: string } | null),
    w: transformMeasurement(mawp?.w as { num: number; unit: string } | null),
    s_y: transformMeasurement(
      mawp?.s_y as { num: number; unit: string } | null
    ),
    tsl: transformMeasurement(
      mawp?.tsl as { num: number; unit: string } | null
    ),
    mawp_c: transformMeasurement(
      mawp?.mawp_c as { num: number; unit: string } | null
    ),
    mawp_l: transformMeasurement(
      mawp?.mawp_l as { num: number; unit: string } | null
    ),
    mawp_design: transformMeasurement(
      mawp?.mawp_design as { num: number; unit: string } | null
    ),
    mawpr: transformMeasurement(
      mawp?.mawpr as { num: number; unit: string } | null
    ),
    calcType: (mawp?.calcType as 'thinWall' | 'thickWall' | null) ?? null,
  }
}

/** Transform Part 4 calculated values from JSON */
function transformPartFourCalculatedValues(
  data: Record<string, unknown>
): FfsPartFourCalculatedValues {
  const calcValues = data.calculated_values as
    | Record<string, unknown>
    | undefined
  return {
    rt: transformMeasurement(
      calcValues?.rt as { num: number; unit: string } | null
    ),
    rsf_a: transformMeasurement(
      calcValues?.rsf_a as { num: number; unit: string } | null
    ),
    q: transformMeasurement(
      calcValues?.q as { num: number; unit: string } | null
    ),
    l: transformMeasurement(
      calcValues?.l as { num: number; unit: string } | null
    ),
    ts_am: transformMeasurement(
      calcValues?.ts_am as { num: number; unit: string } | null
    ),
    tc_am: transformMeasurement(
      calcValues?.tc_am as { num: number; unit: string } | null
    ),
  }
}

/** Transform Part 4 thickness criteria from JSON */
function transformPartFourThicknessCriteria(
  data: Record<string, unknown>
): FfsPartFourThicknessCriteria {
  const criteria = data.thickness_criteria as
    | Record<string, unknown>
    | undefined
  return {
    t_lim: transformMeasurement(
      criteria?.t_lim as { num: number; unit: string } | null
    ),
    t_c_min: transformMeasurement(
      criteria?.t_c_min as { num: number; unit: string } | null
    ),
    t_l_min: transformMeasurement(
      criteria?.t_l_min as { num: number; unit: string } | null
    ),
    thickness_criteria_min: transformMeasurement(
      criteria?.thickness_criteria_min as { num: number; unit: string } | null
    ),
    effective_measured_thickness: transformMeasurement(
      criteria?.effective_measured_thickness as {
        num: number
        unit: string
      } | null
    ),
  }
}

/** Transform Part 5 flaw size criteria from JSON */
function transformPartFiveFlawSizeCriteria(
  data: Record<string, unknown>
): FfsPartFiveFlawSizeCriteria {
  const criteria = data.flaw_size_criteria as
    | Record<string, unknown>
    | undefined
  return {
    rt: transformMeasurement(
      criteria?.rt as { num: number; unit: string } | null
    ),
    tmm_threshold: transformMeasurement(
      criteria?.tmm_threshold as { num: number; unit: string } | null
    ),
    lmsd: transformMeasurement(
      criteria?.lmsd as { num: number; unit: string } | null
    ),
    lmsd_threshold: transformMeasurement(
      criteria?.lmsd_threshold as { num: number; unit: string } | null
    ),
    remainingThicknessThresholdRatio: transformMeasurement(
      criteria?.remainingThicknessThresholdRatio as {
        num: number
        unit: string
      } | null
    ),
  }
}

/** Transform Part 5 final acceptability criteria from JSON */
function transformPartFiveFinalAcceptability(
  data: Record<string, unknown>
): FfsPartFiveFinalAcceptability {
  const criteria = data.final_acceptability_criteria as
    | Record<string, unknown>
    | undefined
  return {
    rsf: transformMeasurement(
      criteria?.rsf as { num: number; unit: string } | null
    ),
    rsf_a: transformMeasurement(
      criteria?.rsf_a as { num: number; unit: string } | null
    ),
    mt: transformMeasurement(
      criteria?.mt as { num: number; unit: string } | null
    ),
    rt_threshold: transformMeasurement(
      criteria?.rt_threshold as { num: number; unit: string } | null
    ),
  }
}

/** Transform full FFS LTA report entry to typed data */
function transformFfsReportEntry(entry: GeckoEntry): FfsLtaReportData {
  const data = entry.data as Record<string, unknown>
  const ffsPart = data.ffs_part as 'part-4' | 'part-5'
  const outcome = data.outcome as FfsLtaReportData['outcome']

  const baseData = {
    id: entry.id,
    outcome,
    ffsLevel: data.ffs_level as '1' | '2',
    ffsPart,
    isDefault: (data.is_default as boolean) ?? false,
    userParams: transformUserParams(data),
    dataValues: transformDataValues(data),
    mawp: transformMawpValues(data),
  }

  if (ffsPart === 'part-4') {
    return {
      ...baseData,
      ffsPart: 'part-4',
      calculatedValues: transformPartFourCalculatedValues(data),
      thicknessCriteria: transformPartFourThicknessCriteria(data),
    } as FfsLtaReportDataPartFour
  } else {
    return {
      ...baseData,
      ffsPart: 'part-5',
      flawSizeCriteria: transformPartFiveFlawSizeCriteria(data),
      finalAcceptabilityCriteria: transformPartFiveFinalAcceptability(data),
    } as FfsLtaReportDataPartFive
  }
}

interface UseIndicationsWithFfsReportsOptions {
  /** Explicit indication IDs to fetch */
  indicationIds: string[]
  /** Inspection IDs to look up indications by tag (used as fallback when indicationIds is empty) */
  inspectionIds?: string[]
}

/**
 * Hook to fetch indications and their associated FFS LTA reports
 *
 * If indicationIds are provided, fetches those specific indications.
 * If indicationIds is empty and inspectionIds are provided, fetches all indications
 * tagged to those inspections.
 *
 * For each indication, also fetches:
 * - The associated ffs-lta-report entry filtered to is_default=true
 */
export function useIndicationsWithFfsReports({
  indicationIds,
  inspectionIds = [],
}: UseIndicationsWithFfsReportsOptions): {
  indicationsWithReports: IndicationWithFfsReport[]
  indicationIdsFromInspection: string[]
  isLoading: boolean
  forceRefresh: () => Promise<void>
} {
  const hasExplicitIndicationIds = indicationIds.length > 0
  const hasInspectionIds = inspectionIds.length > 0

  // Fetch indications by inspection tag (fallback when no explicit indication IDs)
  const {
    entries: indicationsByInspection,
    isLoading: isLoadingIndicationsByInspection,
    forceRefresh: refreshIndicationsByInspection,
  } = useRolodexEntries(
    useMemo(
      () =>
        !hasExplicitIndicationIds && hasInspectionIds
          ? {
              type: RolodexEntryType.INDICATION,
              tags: {
                inspection: inspectionIds,
              },
            }
          : undefined,
      [hasExplicitIndicationIds, hasInspectionIds, inspectionIds]
    )
  )

  // Get indication IDs from inspection lookup (for returning to caller)
  const indicationIdsFromInspection = useMemo(
    () => indicationsByInspection.map((entry) => entry.id),
    [indicationsByInspection]
  )

  // Determine which indication IDs to use for fetching
  const effectiveIndicationIds = useMemo(
    () =>
      hasExplicitIndicationIds ? indicationIds : indicationIdsFromInspection,
    [hasExplicitIndicationIds, indicationIds, indicationIdsFromInspection]
  )

  // Fetch indications by explicit IDs (only if we have explicit IDs)
  const {
    entries: indicationsByIds,
    isLoading: isLoadingIndicationsByIds,
    forceRefresh: refreshIndicationsByIds,
  } = useRolodexEntries(
    useMemo(
      () =>
        hasExplicitIndicationIds
          ? {
              type: RolodexEntryType.INDICATION,
              ids: indicationIds,
            }
          : undefined,
      [hasExplicitIndicationIds, indicationIds]
    )
  )

  // Use either explicit indications or indications by inspection
  const indicationEntries = hasExplicitIndicationIds
    ? indicationsByIds
    : indicationsByInspection

  // Fetch FFS LTA reports for these indications (filtered to is_default=true)
  const {
    entries: ffsReportEntries,
    isLoading: isLoadingFfsReports,
    forceRefresh: refreshFfsReports,
  } = useRolodexEntries(
    useMemo(
      () =>
        effectiveIndicationIds.length > 0
          ? {
              type: FFS_LTA_REPORT_TYPE,
              tags: {
                indication: effectiveIndicationIds,
              },
              filters: [
                {
                  key: 'is_default',
                  op: '=',
                  value: 'true',
                },
              ],
            }
          : undefined,
      [effectiveIndicationIds]
    )
  )

  // Convert entries to our data types and combine
  // Only include indications that have an associated ffs-lta-report
  const indicationsWithReports = useMemo(() => {
    const ffsReportsByIndicationId = new Map<string, FfsLtaReportData>()

    // Index FFS reports by indication ID - transform full report data
    ffsReportEntries.forEach((entry: GeckoEntry) => {
      const indicationId = entry.tags?.indication as string
      if (indicationId) {
        ffsReportsByIndicationId.set(
          indicationId,
          transformFfsReportEntry(entry)
        )
      }
    })

    // Map indications to our format and attach FFS reports
    // Filter out indications that don't have an associated ffs-lta-report
    return indicationEntries
      .filter((entry: GeckoEntry) => ffsReportsByIndicationId.has(entry.id))
      .map((entry: GeckoEntry): IndicationWithFfsReport => {
        const rolodexIndication = entryToIndication(entry.raw)

        const indication: FfsIndicationData = {
          id: entry.id,
          title: rolodexIndication.title,
          selection: rolodexIndication.selection
            ? {
                x: {
                  min: rolodexIndication.selection.x.min,
                  max: rolodexIndication.selection.x.max,
                },
                y: {
                  min: rolodexIndication.selection.y.min,
                  max: rolodexIndication.selection.y.max,
                },
              }
            : null,
          componentId: rolodexIndication.componentId ?? '',
        }

        // ffsReport is guaranteed to exist due to the filter above
        const ffsReport = ffsReportsByIndicationId.get(entry.id)!

        return {
          indication,
          ffsReport,
        }
      })
  }, [indicationEntries, ffsReportEntries])

  const forceRefresh = useMemo(
    () => async () => {
      await Promise.all([
        hasExplicitIndicationIds
          ? refreshIndicationsByIds()
          : refreshIndicationsByInspection(),
        refreshFfsReports(),
      ])
    },
    [
      hasExplicitIndicationIds,
      refreshIndicationsByIds,
      refreshIndicationsByInspection,
      refreshFfsReports,
    ]
  )

  const isLoading =
    (hasExplicitIndicationIds
      ? isLoadingIndicationsByIds
      : isLoadingIndicationsByInspection) || isLoadingFfsReports

  return {
    indicationsWithReports,
    indicationIdsFromInspection,
    isLoading,
    forceRefresh,
  }
}
